import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

import Topbar from "../../../../../components/topbar/TopBar";
import Header from "../../../../../components/header/Header";
import DataTable from "../../../../../components/datatable/DataTable";
import { getTokenRefresh } from "../../../../../helpers/token/refreshToken";
import {
  deleteEmployeeApi,
  getEnterpriseCommentsByEmployeeId,
  getEmployeesFromEnterprise,
  updateEnterpriseComment,
  verifyEmployeeServer,
} from "../../../../../api/users/employee/employeeApi";
import SnackBar from "../../../../../components/snackbar/SnackBar";
import { tokens } from "../../../../../theme";
import { modalStyle } from "../EmployeeStyle";
//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../../components/sidebar/Sidebar";
import {
  StatusEnum,
  StatusEnumName,
} from "../../../../../helpers/constants/Status";
import { UserStatus } from "../../../../../module_firebase/domain/usecases/status/model/UserStatusModel";
import { UpdateUserStateUseCase } from "../../../../../module_firebase/domain/usecases/status/UserStatusUseCase";
import { UsersEnum } from "../../../../../helpers/constants/Users";
import { UpdateStatusEmployeeUseCase } from "../domain/UpdateStatusEmployeeUseCase";

const EmployeeList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const [UID, setUID] = useState("");
  const [email, setEmail] = useState("");
  const [STATUS, setSTATUS] = useState("");
  const [verify, setVerify] = useState(0);
  const { id } = useParams();
  const [employees, setEmployees] = useState([]);
  const location = useLocation();
  const enterpriseName = location.state.enterpriseName;
  const [openRatingsModal, setOpenRatingsModal] = useState(false);
  const [selectedEmployeeRatings, setSelectedEmployeeRatings] = useState([]);
  const [employeeRatingsSearchTerm, setEmployeeRatingsSearchTerm] =
    useState("");
  const [isLoadingRatings, setIsLoadingRatings] = useState(false);
  const [isUpdatingRatings, setIsUpdatingRatings] = useState(false);
  const [openChatModal, setOpenChatModal] = useState(false);

  const visibleEmployeeRatings = selectedEmployeeRatings
    .map((rating, index) => ({ ...rating, originalIndex: index }))
    .filter((rating) => {
      const searchValue = `${employeeRatingsSearchTerm ?? ""}`
        .trim()
        .toLowerCase();
      if (!searchValue) return true;

      return `${rating.order_number ?? ""}`
        .trim()
        .toLowerCase()
        .includes(searchValue);
    });

  const getEmployeeRatingRows = (ratingsSource) => {
    if (!Array.isArray(ratingsSource)) return [];

    return ratingsSource.map((rating) => ({
      ...rating,
      client_name: rating?.name ?? "",
      rating:
        rating?.rating === 0 || rating?.rating
          ? Number(rating.rating).toFixed(1)
          : "",
      comments: rating?.comments ?? "",
    }));
  };

  const validateEmployeeRating = (rating) => {
    const numericRating = Number(rating.rating);
    if (
      rating.rating === "" ||
      Number.isNaN(numericRating) ||
      numericRating < 0 ||
      numericRating > 5
    ) {
      snackBarDialog("LA CALIFICACION DEBE ESTAR ENTRE 0.0 Y 5.0", "error");
      return false;
    }

    if ((rating.comments ?? "").length > 50) {
      snackBarDialog("EL COMENTARIO NO DEBE SUPERAR 50 CARACTERES", "error");
      return false;
    }

    return true;
  };

  const handleOpenRatingsModal = async (employee) => {
    setSelectedEmployeeRatings([]);
    setEmployeeRatingsSearchTerm("");
    setOpenRatingsModal(true);
    setIsLoadingRatings(true);

    try {
      const token = getTokenRefresh();
      const response = await getEnterpriseCommentsByEmployeeId(
        token,
        employee.id,
      );
      setSelectedEmployeeRatings(getEmployeeRatingRows(response?.data?.data));
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO CARGAR LAS CALIFICACIONES", "error");
    } finally {
      setIsLoadingRatings(false);
    }
  };

  const handleRatingsModalClose = () => {
    setOpenRatingsModal(false);
    setSelectedEmployeeRatings([]);
    setEmployeeRatingsSearchTerm("");
  };

  const handleEmployeeRatingFieldChange = (index, field, value) => {
    setSelectedEmployeeRatings((currentRatings) =>
      currentRatings.map((rating, currentIndex) =>
        currentIndex === index ? { ...rating, [field]: value } : rating,
      ),
    );
  };

  const handleUpdateEmployeeRating = async (index) => {
    const currentRating = selectedEmployeeRatings[index];
    if (!currentRating) return;
    if (!validateEmployeeRating(currentRating)) return;

    setIsUpdatingRatings(true);

    try {
      const token = getTokenRefresh();
      const formattedRating = {
        ...currentRating,
        rating: Number(Number(currentRating.rating).toFixed(1)),
        comments: currentRating.comments || null,
      };

      await updateEnterpriseComment(token, {
        id: formattedRating.id,
        employee_id: formattedRating.employee_id,
        name: formattedRating.name,
        rating: formattedRating.rating,
        comments: formattedRating.comments,
        booking_id: formattedRating.booking_id,
      });

      handleEmployeeRatingFieldChange(
        index,
        "rating",
        formattedRating.rating.toFixed(1),
      );
      snackBarDialog("CALIFICACION ACTUALIZADA");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO ACTUALIZAR LA CALIFICACION", "error");
    } finally {
      setIsUpdatingRatings(false);
    }
  };

  const handleChatModalClose = () => setOpenChatModal(false);

  useEffect(() => {
    const getEmployees = async () => {
      try {
        const token = getTokenRefresh();
        const response = await getEmployeesFromEnterprise(token, id);
        console.log(response);
        setEmployees([]);
        let count = 0;
        Object.values(response?.data).map((employee) => {
          if (employee.status !== 3) {
            employee.index = ++count;
            employee.employeeStatus = StatusEnumName(employee.status);
            setEmployees((oldData) => [...oldData, employee]);
          }
        });
      } catch (error) {
        console.log(error);
      }
    };
    getEmployees();
  }, []);

  const columns = [
    { field: "index", headerName: "ID", width: 50 },
    {
      headerName: "NOMBRE",
      field: "name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 130,
    },
    {
      headerName: "APELLIDOS",
      field: "lastname",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
    },
    {
      headerName: "DNI",
      field: "dni",
      width: 100,
    },
    {
      headerName: "EMAIL",
      field: "email",
      width: 200,
    },
    {
      headerName: "TELEFONO",
      field: "phone",
      width: 110,
    },
    {
      headerName: "ROL",
      field: "subrole_name",
      width: 110,
    },
    {
      headerName: "SEDE",
      field: "sede_name",
      width: 110,
    },

    {
      headerName: "ESTADO",
      field: "employeeStatus",
      width: 100,
    },
    {
      headerName: "VERIFICADO",
      headerAlign: "center",
      field: "verified",
      align: "center",
      width: 100,
      renderCell: ({ row: { verified } }) => {
        return <p>{verified == 0 ? "NO" : "SI"}</p>;
      },
    },
    {
      headerName: "CREADO",
      headerAlign: "center",
      field: "created_at",
      align: "center",
      width: 100,
      valueFormatter: ({ value }) =>
        value ? value.slice(0, 10).split("-").reverse().join("/") : "",
    },

    {
      headerName: "R",
      headerAlign: "center",
      field: "rating",
      width: 50,
      align: "center",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Button
            size="small"
            color="secondary"
            variant="contained"
            sx={{ minWidth: 32, p: "2px 6px" }}
            onClick={() => handleOpenRatingsModal(row)}
          >
            {row.rating}
          </Button>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      align: "center",
      renderCell: ({ row: { id, email, status, verified } }) => {
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/employee/${id}`);
              }}
            >
              <BorderColorOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              component="label"
              sx={{ background: "#AA151B" }}
              onClick={() => {
                console.log(id, email);
                setUID(id);
                setEmail(email);
                setSTATUS(status);
                setVerify(verified);
                setOpenModal(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const [openModal, setOpenModal] = useState(false);
  const handleModalClose = () => setOpenModal(false);

  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const vertical = "top";
  const horizontal = "center";
  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const updateStatus = async (status) => {
    try {
      const resp = await UpdateStatusEmployeeUseCase(UID, status, email);

      if (resp.message === "SUCCESS") {
        const userStatus = new UserStatus(UID, UsersEnum.EMPLOYEE, status);
        await UpdateUserStateUseCase(userStatus);

        switch (status) {
          case 1 && 2:
            employees.map((employee) => {
              if (employee.id === UID) {
                employee.status = status;
                employee.employeeStatus = StatusEnumName(status);
              }
            });
            snackBarDialog("ESTADO ACTUALIZADO");
            break;
          case 3:
            setEmployees(employees.filter((employee) => employee.id !== UID));
            snackBarDialog("EMPLEADO ELIMINADO");
            break;
        }
      } else {
        snackBarDialog("ERROR NO SE LOGRO ACTUALIZAR EL ESTADO", "error");
      }

      setIsLoading(false);
      setOpenModal(false);
    } catch (error) {
      snackBarDialog("Error no se podido Suspender", "error");
      console.log(error);
      setIsLoading(false);
    }
  };

  const verifyEmployee = async () => {
    try {
      setIsLoading(true);
      const response = await verifyEmployeeServer(UID);
      employees.map((employee) => {
        if (employee.id === UID) employee.verified = 1;
      });

      setIsLoading(false);
      setOpenModal(false);
      snackBarDialog("EMPLEADO VERIFICADO");
    } catch (error) {
      setIsLoading(false);
      setOpenModal(false);
      console.log(error);
      snackBarDialog("NO SE LOGRO VERIFICAR AL USUARIO", "error");
    }
  };

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="20px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header title={enterpriseName} subtitle="LISTA DE EMPLEADOS" />
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(12, 1fr)"
              gridAutoRows="120px"
              gap="10px"
            >
              <Box gridColumn="span 12" gridRow="span 2">
                <DataTable data={employees} columns={columns} />
              </Box>
            </Box>
            <Modal open={openModal} onClose={handleModalClose}>
              <Box backgroundColor={colors.primary[400]} sx={modalStyle}>
                <Typography variant="h6" component="h2" textAlign={"center"}>
                  ELIMINAR EMPLEADO?
                </Typography>
                <Grid container spacing={1} alignItems="center">
                  {verify == 0 && (
                    <Grid item xs={3} md={3} textAlign="center">
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={verifyEmployee}
                        disabled={isLoading}
                      >
                        VERIFICAR
                      </Button>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={verify == 1 ? 4 : 3}
                    md={verify == 1 ? 4 : 3}
                    textAlign="center"
                  >
                    {STATUS === 1 ? (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => updateStatus(StatusEnum.SUSPENDIDO)}
                        disabled={isLoading}
                        fullWidth
                      >
                        SUSPENDER
                      </Button>
                    ) : (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => updateStatus(StatusEnum.ACTIVO)}
                        disabled={isLoading}
                        fullWidth
                      >
                        HABILITAR
                      </Button>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={verify == 1 ? 4 : 3}
                    md={verify == 1 ? 4 : 3}
                    textAlign="center"
                  >
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => updateStatus(StatusEnum.ELIMINADO)}
                      disabled={isLoading}
                      fullWidth
                    >
                      ELIMINAR
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={verify == 1 ? 4 : 3}
                    md={verify == 1 ? 4 : 3}
                    textAlign="center"
                  >
                    <Button
                      color="error"
                      variant="contained"
                      onClick={handleModalClose}
                      disabled={isLoading}
                      fullWidth
                    >
                      CANCELAR
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Modal>
            <Modal
              open={openRatingsModal}
              onClose={handleRatingsModalClose}
              aria-labelledby="modal-ratings-title"
            >
              <Box
                sx={{
                  ...modalStyle,
                  width: { xs: "90%", sm: 760 },
                  maxHeight: "80vh",
                  overflowY: "auto",
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography
                  id="modal-ratings-title"
                  variant="h5"
                  component="h2"
                  textAlign="center"
                  sx={{ fontWeight: 900 }}
                  mb={2}
                >
                  Lista de Calificaciones
                </Typography>
                {isLoadingRatings ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress color="secondary" />
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    <TextField
                      value={employeeRatingsSearchTerm}
                      fullWidth
                      label="Buscar pedido"
                      variant="outlined"
                      size="small"
                      onChange={(event) =>
                        setEmployeeRatingsSearchTerm(event.target.value)
                      }
                    />
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        borderRadius: 1,
                        border: "1px solid rgba(255, 255, 255, 0.18)",
                        overflowX: "auto",
                        backgroundColor: colors.primary[500],
                      }}
                    >
                      <Table size="small" aria-label="tabla de calificaciones">
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: colors.primary[600],
                            }}
                          >
                            <TableCell sx={{ fontWeight: 800 }}>
                              PEDIDO
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>
                              CLIENTE
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, minWidth: 90 }}>
                              CALIFICACION
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, minWidth: 230 }}>
                              COMENTARIO
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              ACCIONES
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleEmployeeRatings.length > 0 ? (
                            visibleEmployeeRatings.map((rating) => (
                              <TableRow
                                key={`${rating.order_number}-${rating.originalIndex}`}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    borderBottom: 0,
                                  },
                                }}
                              >
                                <TableCell>{rating.order_number}</TableCell>
                                <TableCell>{rating.client_name}</TableCell>
                                <TableCell>
                                  <TextField
                                    value={rating.rating ?? ""}
                                    fullWidth
                                    type="number"
                                    label="Calificacion"
                                    variant="outlined"
                                    margin="dense"
                                    inputProps={{
                                      min: 0,
                                      max: 5,
                                      step: 0.1,
                                    }}
                                    disabled={isUpdatingRatings}
                                    onChange={(event) =>
                                      handleEmployeeRatingFieldChange(
                                        rating.originalIndex,
                                        "rating",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    value={rating.comments ?? ""}
                                    fullWidth
                                    label="Comentario"
                                    variant="outlined"
                                    margin="dense"
                                    inputProps={{ maxLength: 50 }}
                                    disabled={isUpdatingRatings}
                                    onChange={(event) =>
                                      handleEmployeeRatingFieldChange(
                                        rating.originalIndex,
                                        "comments",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      color="secondary"
                                      variant="contained"
                                      disabled={isUpdatingRatings}
                                      onClick={() =>
                                        handleUpdateEmployeeRating(
                                          rating.originalIndex,
                                        )
                                      }
                                    >
                                      {isUpdatingRatings
                                        ? "ACTUALIZANDO..."
                                        : "ACTUALIZAR"}
                                    </Button>
                                    <IconButton
                                      color="success"
                                      aria-label="chat"
                                      sx={{ background: "#ffff" }}
                                      onClick={() => setOpenChatModal(true)}
                                    >
                                      <ChatOutlinedIcon />
                                    </IconButton>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography textAlign="center" py={2}>
                                  No hay calificaciones registradas.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                )}
              </Box>
            </Modal>
            <Modal
              open={openChatModal}
              onClose={handleChatModalClose}
              aria-labelledby="modal-chat-title"
            >
              <Box
                sx={{
                  ...modalStyle,
                  width: { xs: "90%", sm: 420 },
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography
                  id="modal-chat-title"
                  variant="h5"
                  component="h2"
                  textAlign="center"
                  sx={{ fontWeight: 900 }}
                >
                  Chat del Pedido
                </Typography>
              </Box>
            </Modal>
            <SnackBar
              stateSnk={stateSnk}
              vertical={vertical}
              horizontal={horizontal}
              duration={5000}
              handleClose={handleSnkClose}
              severity={severity}
              messageSnk={messageSnk}
            />
          </Box>
        </main>
      </div>
    </>
  );
};

export default EmployeeList;
